/* global themeVars */
import { trackContentEngagement } from '../../js/front/analytics';

document.querySelectorAll( '.b-winners' ).forEach( ( block ) => {

	const body = document.querySelector( 'body' );
	const winnersWrapper = block.querySelector( '.b-winners__container' );
	const loadMoreButton = block.querySelector( '.js-load-more-winners' );
	const loadMoreWrapper = block.querySelector( '.b-winners__button-wrapper' );
	const openFiltersButton = block.querySelector( '.js-open-filters' );
	const closeFiltersButton = block.querySelector( '.js-close-filters' );
	const filterWinnersButton = block.querySelector( '.js-filter-winners' );
	const clearFiltersButton = block.querySelector( '.js-clear-filters-button' );
	const appliedFiltersContainer = block.querySelector( '.applied-filters' );
	const appliedFiltersList = block.querySelector( '.js-selected-filters-list' );
	const filtersForm = block.querySelector( '.filters-wrapper' );
	const winnersFiltersDropdowns = block.querySelectorAll( '.winners-filters-dropdown' );
	const mobileBreakpoint = '1140px';
	let isLoading = false;
	let shouldFocusFirstNewWinner = false;
	let lastFocusedBeforeFilters = null;
	const ownerDocument = block.ownerDocument;

	const focusableSelector = [
		'a[href]',
		'button:not([disabled])',
		'input:not([disabled]):not([type="hidden"])',
		'[tabindex]:not([tabindex="-1"])',
	].join( ',' );

	/**
	 * Get focusable elements inside filters overlay.
	 *
	 * @return {HTMLElement[]}
	 */
	const getFocusableElementsInFilters = () => {

		if ( ! filtersForm ) {

			return [];

		}

		return Array.from( filtersForm.querySelectorAll( focusableSelector ) )
			.filter( ( element ) => ! element.hasAttribute( 'disabled' ) && element.getAttribute( 'aria-hidden' ) !== 'true' );

	};

	/**
	 * Deactivate filters overlay and release focus trap.
	 *
	 * @return {void}
	 */
	const deactivateFiltersOverlay = () => {

		if ( ! filtersForm || ! body ) {

			return;

		}

		filtersForm.classList.remove( 'filters-wrapper--active' );
		filtersForm.removeAttribute( 'role' );
		filtersForm.removeAttribute( 'aria-modal' );
		openFiltersButton?.setAttribute( 'aria-expanded', 'false' );
		body.classList.remove( 'body-no-scroll' );
		ownerDocument.removeEventListener( 'keydown', handleFiltersKeydown );

		if ( lastFocusedBeforeFilters instanceof HTMLElement ) {

			lastFocusedBeforeFilters.focus();

		}

		lastFocusedBeforeFilters = null;

	};

	/**
	 * Handle keyboard interactions inside active filters overlay — Tab loop and Escape to close.
	 *
	 * @param {KeyboardEvent} event Keyboard event.
	 * @return {void}
	 */
	const handleFiltersKeydown = ( event ) => {

		if ( ! filtersForm?.classList.contains( 'filters-wrapper--active' ) ) {

			return;

		}

		if ( event.key === 'Escape' ) {

			const activeDropdownWrapper = filtersForm.querySelector( '.winners-filters-dropdown__list-wrapper--active' );
			const activeDropdownButton = activeDropdownWrapper?.closest( '.winners-filters-dropdown' )?.querySelector( '.winners-filters-dropdown__button' );

			if ( activeDropdownButton instanceof HTMLButtonElement ) {

				event.preventDefault();
				event.stopPropagation();
				activeDropdownButton.click();

				return;

			}

			event.preventDefault();
			deactivateFiltersOverlay();

			return;

		}

		if ( event.key !== 'Tab' ) {

			return;

		}

		const focusableElements = getFocusableElementsInFilters();

		if ( ! focusableElements.length ) {

			return;

		}

		const firstFocusable = focusableElements[0];
		const lastFocusable = focusableElements[ focusableElements.length - 1 ];
		const activeElement = ownerDocument.activeElement;
		const isFocusOutsideFilters = ! filtersForm.contains( activeElement );

		if ( event.shiftKey ) {

			if ( activeElement === firstFocusable || isFocusOutsideFilters ) {

				event.preventDefault();
				lastFocusable.focus();

			}

			return;

		}

		if ( activeElement === lastFocusable || isFocusOutsideFilters ) {

			event.preventDefault();
			firstFocusable.focus();

		}

	};

	/**
	 * Activate filters overlay and focus trap.
	 *
	 * @return {void}
	 */
	const activateFiltersOverlay = () => {

		if ( ! filtersForm || ! body ) {

			return;

		}

		lastFocusedBeforeFilters = ownerDocument.activeElement;
		filtersForm.classList.add( 'filters-wrapper--active' );
		filtersForm.setAttribute( 'role', 'dialog' );
		filtersForm.setAttribute( 'aria-modal', 'true' );
		openFiltersButton?.setAttribute( 'aria-expanded', 'true' );
		body.classList.add( 'body-no-scroll' );
		ownerDocument.addEventListener( 'keydown', handleFiltersKeydown );

		const focusableElements = getFocusableElementsInFilters();

		if ( focusableElements.length ) {

			focusableElements[0].focus();

		}

	};

	// Filters overlay is a mobile/tablet-only pattern (desktop shows the sidebar inline, statically).
	// If the viewport crosses into desktop while the overlay is active, drop the modal state so
	// role="dialog"/aria-modal/aria-expanded/focus-trap/no-scroll don't linger on the static sidebar.
	const desktopMediaQuery = window.matchMedia( `(min-width: ${ mobileBreakpoint })` );

	desktopMediaQuery.addEventListener( 'change', ( event ) => {

		if ( event.matches && filtersForm?.classList.contains( 'filters-wrapper--active' ) ) {

			deactivateFiltersOverlay();

		}

	} );

	if ( filtersForm && openFiltersButton && body ) {

		openFiltersButton.addEventListener( 'click', () => {

			activateFiltersOverlay();

		} );

	}

	if ( filtersForm && closeFiltersButton && body ) {

		closeFiltersButton.addEventListener( 'click', () => {

			deactivateFiltersOverlay();

		} );

	}

	if ( filtersForm && filterWinnersButton && body ) {

		filterWinnersButton.addEventListener( 'click', () => {

			deactivateFiltersOverlay();

		} );

	}

	if ( filtersForm && clearFiltersButton && body ) {

		clearFiltersButton.addEventListener( 'click', () => {

			deactivateFiltersOverlay();

		} );

	}

	/**
	 * Build selected checkbox and radio filters list.
	 *
	 * @return {Array<{name: string, value: string}>}
	 */
	const getSelectedChoiceFilters = () => {

		const defaultChoiceValues = {
			'sort-by': filtersForm?.querySelector( 'input[name="sort-by"]' )?.value || '',
			'prize-amount': filtersForm?.querySelector( 'input[name="prize-amount"]' )?.value || '',
			'game-name': filtersForm?.querySelector( 'input[name="game-name"]' )?.value || '',
		};

		const selectedInputs = Array.from( filtersForm?.querySelectorAll( 'input[type="checkbox"]:checked, input[type="radio"]:checked' ) || [] )
			.map( ( input ) => {

				const fieldName = input.name.replace( /\[\]$/, '' );
				const fieldValue = input.value;
				const defaultValue = defaultChoiceValues[ fieldName ];

				if ( defaultValue && fieldValue === defaultValue ) {

					return null;

				}

				return {
					name: fieldName,
					value: fieldValue,
				};

			} );

		return selectedInputs.filter( ( item ) => item !== null );

	};

	/**
	 * Build sanitized text filters from form.
	 *
	 * @return {Array<{name: string, value: string}>}
	 */
	const getFilledTextFilters = () => {

		const textFilterNames = [ 'retailer-city', 'zip-code', 'retailer-name' ];
		const selectedPrizeAmount = filtersForm?.querySelector( 'input[name="prize-amount"]:checked' )?.value || '';

		// Min/max prize amount only apply when 'custom-amount' is the selected prize filter.
		if ( selectedPrizeAmount === 'custom-amount' ) {

			textFilterNames.push( 'minimum-prize-amount', 'maximum-prize-amount' );

		}

		return textFilterNames
			.map( ( fieldName ) => {

				const input = filtersForm?.querySelector( `input[name="${ fieldName }"]` );
				const inputValue = input?.value || '';

				if ( inputValue === '' ) {

					return null;

				}

				return {
					name: fieldName,
					value: inputValue,
				};

			} )
			.filter( ( item ) => item !== null );

	};

	/**
	 * Build URL params from current filter form state.
	 *
	 * @return {URLSearchParams}
	 */
	const buildFilterParams = () => {

		const params = new URLSearchParams();
		const selectedChoiceFilters = getSelectedChoiceFilters();
		const sanitizedTextFilters = getFilledTextFilters();

		selectedChoiceFilters.forEach( ( item ) => {

			params.append( item.name, item.value );

		} );

		sanitizedTextFilters.forEach( ( item ) => {

			params.append( item.name, item.value );

		} );

		return params;

	};

	/**
	 * Synchronize browser URL with current filters without page reload.
	 *
	 * @param {URLSearchParams} params Query params.
	 * @return {void}
	 */
	const syncUrlWithFilters = ( params ) => {

		const currentUrl = new URL( window.location.href );
		// Remove comma from url
		const queryString = params.toString().replace( /%2C/g, '' );
		const nextUrl = `${ currentUrl.origin }${ currentUrl.pathname }${ queryString ? `?${ queryString }` : '' }`;
		window.history.pushState( {}, '', nextUrl );

	};

	/**
	 * Render applied filters chips in sidebar.
	 *
	 * @param {Array<{name: string, value: string}>} selectedChoiceFilters Selected choice filters.
	 * @param {Array<{name: string, value: string}>} sanitizedTextFilters Selected text filters.
	 * @return {void}
	 */
	const renderAppliedFilters = ( selectedChoiceFilters, sanitizedTextFilters ) => {

		if ( ! appliedFiltersList ) {

			return;

		}

		const allSelectedFilters = [ ...selectedChoiceFilters, ...sanitizedTextFilters ];
		const visibleSelectedFilters = allSelectedFilters.filter( ( selectedFilter ) => selectedFilter.name !== 'sort-by' );
		appliedFiltersList.replaceChildren();

		visibleSelectedFilters.forEach( ( selectedFilter ) => {

			// Escape free-typed values (retailer-city/name, zip-code) so they can't break the attribute selector.
			const escapedName = CSS.escape( selectedFilter.name );
			const escapedValue = CSS.escape( selectedFilter.value );
			const matchingInput = filtersForm?.querySelector( `input[name="${ escapedName }"][value="${ escapedValue }"]` )
				|| filtersForm?.querySelector( `input[name="${ escapedName }[]"][value="${ escapedValue }"]` );

			const filterLabel = matchingInput?.dataset?.filtername || selectedFilter.value;
			const filterButton = ownerDocument.createElement( 'button' );
			const labelElement = ownerDocument.createElement( 'span' );
			const closeIcon = ownerDocument.createElement( 'span' );
			filterButton.type = 'button';
			filterButton.className = 'applied-filters__item selected-filter-button';
			filterButton.dataset.filter = selectedFilter.name;
			filterButton.dataset.filtervalue = selectedFilter.value;
			labelElement.textContent = filterLabel;
			closeIcon.className = 'close-icon';
			filterButton.append( labelElement, closeIcon );

			appliedFiltersList.appendChild( filterButton );

		} );

		const hasActiveFilters = visibleSelectedFilters.length > 0;

		if ( appliedFiltersContainer ) {

			appliedFiltersContainer.hidden = ! hasActiveFilters;

		}

		if ( clearFiltersButton ) {

			clearFiltersButton.hidden = ! hasActiveFilters;

		}

	};

	/**
	 * Build empty state message for winners list.
	 *
	 * @param {boolean} hasActiveFilters Whether any filters are active.
	 * @return {string}
	 */
	const getEmptyStateInfo = ( hasActiveFilters ) => {

		if ( !winnersWrapper ) {

			return '';

		}

		const noFiltersDataText = winnersWrapper.dataset.noFiltersDataText || 'No winners found for the selected filters.';
		const noDataEmptyText = winnersWrapper.dataset.noDataEmptyText || 'No data available.';

		const emptyStateText = hasActiveFilters
			? noFiltersDataText
			: noDataEmptyText;

		return `<p class="empty-winners">${ emptyStateText }</p>`;

	};

	/**
	 * Replace winners container content using parsed DOM nodes.
	 *
	 * @param {string} htmlString HTML string returned by backend.
	 * @param {boolean} hasActiveFilters Whether any filters are active.
	 * @return {void}
	 */
	const replaceWinnersContent = ( htmlString, hasActiveFilters ) => {

		const htmlToRender = htmlString || getEmptyStateInfo( hasActiveFilters );
		const parsedFragment = ownerDocument.createRange().createContextualFragment( htmlToRender );

		winnersWrapper.replaceChildren( ...parsedFragment.childNodes );

	};

	/**
	 * Apply current form filters without full page reload.
	 *
	 * @param {string} interactionMethod How the filters were changed: filters_form, remove_filter or clear_filters.
	 * @return {void}
	 */
	const applyFilters = ( interactionMethod ) => {

		const selectedChoiceFilters = getSelectedChoiceFilters();
		const sanitizedTextFilters = getFilledTextFilters();
		const params = buildFilterParams();

		renderAppliedFilters( selectedChoiceFilters, sanitizedTextFilters );
		syncUrlWithFilters( params );
		fetchPosts( false, true );
		// Filter values may contain a retailer name, city or ZIP entered by the visitor, so only the action is sent.
		trackContentEngagement( block, 'winners_filter', interactionMethod );

	};

	// Add event listener to filters form submit to update the URL with selected filters.
	if ( filtersForm ) {

		filtersForm.addEventListener( 'submit', ( event ) => {

			event.preventDefault();
			applyFilters( 'filters_form' );

		} );

	}

	// Add event listener to applied filters list to remove a filter and update the URL.
	if ( appliedFiltersList && filtersForm ) {

		appliedFiltersList.addEventListener( 'click', ( event ) => {

			const selectedFilterButton = event.target.closest( '.selected-filter-button' );
			const filterName = selectedFilterButton?.dataset.filter;
			const filterValue = selectedFilterButton?.dataset.filtervalue;

			if ( ! selectedFilterButton || ! filterName || ! filterValue ) {

				return;

			}

			event.preventDefault();

			const textInput = filtersForm.querySelector( `input[name="${ filterName }"]` );

			// If the filter is a text input, clear its value and apply filters.
			if ( textInput?.type === 'text' ) {

				textInput.value = '';
				applyFilters( 'remove_filter' );

				return;

			}

			const selectableInput = filtersForm.querySelector( `input[name="${ filterName }"][value="${ filterValue }"]` )
				|| filtersForm.querySelector( `input[name="${ filterName }[]"][value="${ filterValue }"]` );

			if ( ! selectableInput ) {

				return;

			}

			// If the filter is a checkbox or radio input, uncheck it and apply filters.
			if ( selectableInput.type === 'checkbox' ) {

				selectableInput.checked = false;

			}

			if ( selectableInput.type === 'radio' ) {

				const defaultRadio = filtersForm.querySelector( `input[name="${ filterName }"]` );

				if ( defaultRadio ) {

					defaultRadio.checked = true;

					// Setting .checked via JS doesn't fire 'change', so the dropdown label won't refresh on its own.
					const parentDropdown = defaultRadio.closest( '.winners-filters-dropdown' );

					if ( parentDropdown ) {

						syncDropdownChoiceState( parentDropdown );

					}

				}

			}

			applyFilters( 'remove_filter' );

		} );

	}

	// 'Clear all filters' button resets the form and navigates to the base winners page.
	if ( clearFiltersButton && filtersForm ) {

		clearFiltersButton.addEventListener( 'click', ( event ) => {

			event.preventDefault();

			filtersForm.querySelectorAll( 'input[type="checkbox"], input[type="radio"]' ).forEach( ( input ) => {

				input.checked = false;

			} );

			filtersForm.querySelectorAll( 'input[type="text"]' ).forEach( ( input ) => {

				input.value = '';

			} );

			[ 'sort-by', 'prize-amount', 'game-name' ].forEach( ( fieldName ) => {

				const defaultInput = filtersForm.querySelector( `input[name="${ fieldName }"]` );

				if ( defaultInput ) {

					defaultInput.checked = true;

				}

			} );

			winnersFiltersDropdowns.forEach( ( dropdown ) => {

				syncDropdownChoiceState( dropdown );

			} );

			applyFilters( 'clear_filters' );

		} );

	}

	/**
	 * Sync dropdown option state with selected checkbox.
	 *
	 * @param {HTMLElement} dropdown Dropdown wrapper element.
	 * @return {void}
	 */
	const syncDropdownChoiceState = ( dropdown ) => {

		const selectedLabel = dropdown.querySelector( '.selected-option' );
		const selectedInput = dropdown.querySelector( 'input:checked' );
		const selectedName = selectedInput?.dataset?.filtername;

		if ( selectedLabel && selectedName ) {

			selectedLabel.textContent = selectedName;

		}

	};

	// Sync dropdowns on page load.
	winnersFiltersDropdowns.forEach( ( dropdown ) => {

		syncDropdownChoiceState( dropdown );

		const dropdownList = dropdown.querySelector( '.winners-filters-dropdown__list' );
		const dropdownListWrapper = dropdown.querySelector( '.winners-filters-dropdown__list-wrapper' );
		const dropdownButton = dropdown.querySelector( '.winners-filters-dropdown__button' );

		if ( ! dropdownList || ! dropdownButton || ! dropdownListWrapper ) {

			return;

		}

		const dropdownChoices = Array.from( dropdownList.querySelectorAll( 'input[type="checkbox"], input[type="radio"]' ) );

		let dropdownInteractionController = null;

		/**
		 * Toggle whether dropdown options are reachable with Tab.
		 *
		 * @param {boolean} isOpen Whether dropdown is open.
		 * @return {void}
		 */
		const setDropdownTabState = ( isOpen ) => {

			dropdownListWrapper.setAttribute( 'aria-hidden', isOpen ? 'false' : 'true' );

			dropdownChoices.forEach( ( choiceInput ) => {

				if ( isOpen ) {

					choiceInput.removeAttribute( 'tabindex' );

					return;

				}

				choiceInput.setAttribute( 'tabindex', '-1' );

			} );

		};

		/**
		 * Handle keyboard navigation inside an open dropdown.
		 *
		 * @param {KeyboardEvent} event Keyboard event.
		 * @return {void}
		 */
		const handleDropdownTabKeydown = ( event ) => {

			if ( event.key === 'Escape' ) {

				event.preventDefault();
				event.stopPropagation();
				closeDropdown();

				return;

			}

			if ( event.key !== 'Tab' ) {

				return;

			}

			const focusableElements = [
				dropdownButton,
				...dropdownChoices,
			].filter( ( element ) => element.getAttribute( 'tabindex' ) !== '-1' );

			if ( ! focusableElements.length ) {

				return;

			}

			const firstFocusable = focusableElements[0];
			const lastFocusable = focusableElements[ focusableElements.length - 1 ];
			const activeElement = ownerDocument.activeElement;

			if ( event.shiftKey ) {

				if ( activeElement === firstFocusable || ! dropdown.contains( activeElement ) ) {

					event.preventDefault();
					lastFocusable.focus();

				}

				return;

			}

			if ( activeElement === lastFocusable || ! dropdown.contains( activeElement ) ) {

				event.preventDefault();
				firstFocusable.focus();

			}

		};

		/**
		 * Close dropdown and remove outside click listener.
		 *
		 * @return {void}
		 */
		const closeDropdown = () => {

			dropdownListWrapper.classList.remove( 'winners-filters-dropdown__list-wrapper--active' );
			dropdownButton.setAttribute( 'aria-expanded', 'false' );
			setDropdownTabState( false );

			if ( dropdownInteractionController ) {

				dropdownInteractionController.abort();
				dropdownInteractionController = null;

			}

		};

		/**
		 * Open dropdown and attach outside click listener.
		 *
		 * @return {void}
		 */
		const openDropdown = () => {

			dropdownListWrapper.classList.add( 'winners-filters-dropdown__list-wrapper--active' );
			dropdownButton.setAttribute( 'aria-expanded', 'true' );
			setDropdownTabState( true );

			dropdownInteractionController = new AbortController();

			ownerDocument.addEventListener( 'click', ( event ) => {

				const clickTarget = event.target;

				if ( ! ( clickTarget instanceof Element ) ) {

					return;

				}

				if ( ! dropdown.contains( clickTarget ) ) {

					closeDropdown();

				}

			}, { signal: dropdownInteractionController.signal } );

			ownerDocument.addEventListener( 'keydown', handleDropdownTabKeydown, { capture: true, signal: dropdownInteractionController.signal } );

		};

		setDropdownTabState( false );

		dropdownButton.addEventListener( 'click', () => {

			if ( dropdownListWrapper.classList.contains( 'winners-filters-dropdown__list-wrapper--active' ) ) {

				closeDropdown();

				return;

			}

			openDropdown();

		} );

		dropdownList.addEventListener( 'change', ( event ) => {

			const changeTarget = event.target;

			if ( ! ( changeTarget instanceof HTMLInputElement ) || ! [ 'checkbox', 'radio' ].includes( changeTarget.type ) ) {

				return;

			}

			syncDropdownChoiceState( dropdown );

		} );

		dropdownList.addEventListener( 'pointerup', ( event ) => {

			const pointerTarget = event.target;

			if ( ! ( pointerTarget instanceof HTMLInputElement ) || ! [ 'checkbox', 'radio' ].includes( pointerTarget.type ) ) {

				return;

			}

			closeDropdown();

		} );

	} );

	if ( ! winnersWrapper || ! loadMoreButton || ! loadMoreWrapper ) {

		return;

	}

	let liveRegion = block.querySelector( '.js-winners-live-region' );

	/**
	 * Announce newly loaded winner cards to screen readers.
	 *
	 * @param {number} loadedWinnersCount Number of newly appended winner cards.
	 * @return {void}
	 */
	const announceLoadedWinners = ( loadedWinnersCount ) => {

		if ( loadedWinnersCount <= 0 ) {

			return;

		}

		if ( ! liveRegion ) {

			liveRegion = ownerDocument.createElement( 'p' );
			liveRegion.className = 'screen-reader-text js-winners-live-region';
			liveRegion.setAttribute( 'aria-live', 'polite' );
			liveRegion.setAttribute( 'aria-atomic', 'true' );

		}

		if ( loadMoreWrapper.parentElement ) {

			loadMoreWrapper.insertAdjacentElement( 'afterend', liveRegion );

		}

		liveRegion.textContent = `${ loadedWinnersCount } more winner${ loadedWinnersCount === 1 ? '' : 's' } loaded.`;

	};

	/**
	 * Focus first newly appended winner card.
	 *
	 * @param {number} previousWinnersCount Number of cards before append.
	 * @return {void}
	 */
	const focusFirstNewWinnerCard = ( previousWinnersCount ) => {

		const winnerCards = winnersWrapper.querySelectorAll( '.c-winner-item' );
		const firstNewCard = winnerCards[ previousWinnersCount ];

		if ( ! firstNewCard ) {

			return;

		}

		firstNewCard.setAttribute( 'tabindex', '-1' );
		firstNewCard.focus();

		firstNewCard.addEventListener( 'blur', () => {

			firstNewCard.removeAttribute( 'tabindex' );

		}, { once: true } );

	};

	/**
	 * Fetch winners and update the list.
	 *
	 * @param {boolean} focusAfterAppend Whether to move focus to first new winner.
	 * @param {boolean} replaceResults Whether to replace the list (filters submit) instead of append (load more).
	 * @return {void}
	 */
	const fetchPosts = ( focusAfterAppend = false, replaceResults = false ) => {

		if ( isLoading ) {

			return;

		}

		isLoading = true;
		loadMoreButton.disabled = true;

		const winnersPerPage = parseInt( winnersWrapper.dataset.winnersPerPage || '9', 10 );

		const requestUrl = new URL( themeVars.winnersRestUrl );

		requestUrl.searchParams.set( 'offset', replaceResults ? '0' : String( winnersWrapper.dataset.offset || '0' ) );
		requestUrl.searchParams.set( 'per_page', String( winnersPerPage ) );
		requestUrl.searchParams.set( 'big-winner-threshold', String( winnersWrapper.dataset.bigWinnerThreshold || '25000' ) );

		const currentParams = new URLSearchParams( window.location.search );
		const singleFilterKeys = [ 'sort-by', 'prize-amount', 'minimum-prize-amount', 'maximum-prize-amount', 'retailer-city', 'retailer-name', 'zip-code', 'game-name' ];
		const arrayFilterKeys = [ 'time-frame', 'type' ];

		singleFilterKeys.forEach( ( key ) => {

			const value = currentParams.get( key );

			if ( value ) {

				requestUrl.searchParams.set( key, value );

			}

		} );

		arrayFilterKeys.forEach( ( key ) => {

			currentParams.getAll( key ).forEach( ( value ) => {

				if ( value ) {

					requestUrl.searchParams.append( `${ key }[]`, value );

				}

			} );

		} );

		fetch( requestUrl.toString() )
			.then( ( response ) => {

				if ( ! response.ok ) {

					throw new Error( 'request_failed' );

				}

				return response.json();

			} )
			.then( ( result ) => {

				if ( replaceResults ) {

					const hasFilteredResults = Boolean( result.html );
					const hasActiveFilters = window.location.search.replace( /^\?/, '' ) !== '';

					replaceWinnersContent( hasFilteredResults ? result.html : '', hasActiveFilters );

				} else if ( result.html ) {

					const previousWinnersCount = winnersWrapper.querySelectorAll( '.c-winner-item' ).length;

					winnersWrapper.insertAdjacentHTML( 'beforeend', result.html );
					const loadedWinnersCount = winnersWrapper.querySelectorAll( '.c-winner-item' ).length - previousWinnersCount;
					announceLoadedWinners( loadedWinnersCount );

					if ( focusAfterAppend ) {

						focusFirstNewWinnerCard( previousWinnersCount );

					}

				}

				const offset = replaceResults && ! result.html ? 0 : result.next_offset;
				winnersWrapper.dataset.offset = String( offset );

				if ( result.has_more ) {

					loadMoreWrapper.classList.remove( 'button-hidden' );
					loadMoreWrapper.style.display = '';

				} else {

					loadMoreWrapper.classList.add( 'button-hidden' );
					loadMoreWrapper.style.display = 'none';


				}

			} )
			.catch( () => {
				// Keep this silent and allow retry by another click.
			} )
			.finally( () => {

				isLoading = false;
				loadMoreButton.disabled = false;

			} );

	};

	loadMoreButton.addEventListener( 'keydown', ( event ) => {

		if ( event.key === 'Enter' || event.key === ' ' ) {

			shouldFocusFirstNewWinner = true;

		}

	} );

	loadMoreButton.addEventListener( 'click', () => {

		fetchPosts( shouldFocusFirstNewWinner );
		shouldFocusFirstNewWinner = false;
		trackContentEngagement( block, 'load_more', 'load_more_button' );

	} );

} );
